import { useCallback } from "react"
import { useEventAction } from "src/global/hooks/useEventAction"
import { EventTypes } from "src/types/events"

/**
 * Event types that trigger data health refetches
 */
export const DATA_HEALTH_EVENT_TYPES = [
  EventTypes.DATA_ASSET.CREATED,
  EventTypes.DATA_ASSET.DELETED,
  EventTypes.EXPECTATION.CREATED,
  EventTypes.EXPECTATION.DELETED,
  EventTypes.VALIDATION_RESULT.CREATED,
] as const

/**
 * Hook that listens for events that affect data health metrics and automatically refetches data.
 * This is used to keep the DataHealthDashboard in sync with real-time changes.
 *
 * @param onEvent - Function to run when an event is received
 * @param bufferDelay - Optional delay to buffer multiple events (default: 2000ms)
 */
export const useDataHealthEventRefetch = <T = void>(onEvent: () => T, bufferDelay = 2000): void => {
  const handleOnEvent = useCallback(() => {
    onEvent()
  }, [onEvent])

  for (let i = 0; i < DATA_HEALTH_EVENT_TYPES.length; i++) {
    /**
     * IMPORTANT: This loop is safe because DATA_HEALTH_EVENT_TYPES is a static constant array
     * that never changes in length or content during runtime. This guarantees that hooks are
     * called in the same order on every render, satisfying React's rules of hooks.
     *
     * DO NOT modify DATA_HEALTH_EVENT_TYPES to be dynamic or conditional!
     * If you need to change the events, update the array definition but keep it static.
     */
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEventAction(DATA_HEALTH_EVENT_TYPES[i], handleOnEvent, { bufferDelay })
  }
}
